import * as React from "react";
import { Popover as PopoverPrimitive } from "radix-ui";

import { cn } from "@shared/lib/cn";

/*
 * Всплывающий слой.
 *
 * Взят не ради вида, а ради очереди. Esc закрывает верхний слой, щелчок мимо
 * закрывает свой, фокус уходит внутрь и возвращается на кнопку. Своими
 * `document.addEventListener` это было написано трижды и каждый раз чуть
 * иначе — а два стека Esc не уживаются: закрывается тот, кто подписался
 * первым, а не тот, кто сверху.
 *
 * Своей поверхности `PopoverContent` не даёт вовсе, и это решение. Внутрь
 * кладутся уже нарисованные панели системы — `.emoji-panel`, `.call-picker`, —
 * у них свой фон, рамка и тень. Вторая поверхность под ними дала бы двойную
 * рамку и двойную тень.
 */
const Popover = PopoverPrimitive.Root;
const PopoverTrigger = PopoverPrimitive.Trigger;

/**
 * К чему прижимается панель, если не к кнопке.
 *
 * Панель знаков встаёт по правому краю поля ввода, а не по краю кнопки: так
 * она нарисована, и так же открывается из ряда реакций — с точностью до
 * пикселя. Кнопка при этом остаётся кнопкой: за `aria-expanded` и возврат
 * фокуса отвечает она.
 */
const PopoverAnchor = PopoverPrimitive.Anchor;

function PopoverContent({
  className,
  align = "center",
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        data-slot="popover-content"
        align={align}
        sideOffset={sideOffset}
        /* Тот же слой, что у меню и списка выбора: порядок задаёт порядок в DOM. */
        className={cn(
          /*
           * Появление — только масштабом, пружиной из макета v6, без
           * растворения: внутри стоят стеклянные панели, а полупрозрачный
           * предок отрезает стеклу фон, и оно мигнуло бы пустым.
           */
          "z-40 outline-none glass-pop-scale",
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}

export { Popover, PopoverAnchor, PopoverContent, PopoverTrigger };
